<template>
  <div v-if="!bare" class="col-md-6 provider-select">
    <label class="form-label">Proveedor</label>
    <div class="provider-select__field">
      <input
        ref="inputEl"
        v-model="inputValue"
        class="form-control"
        :readonly="disabled"
        :placeholder="placeholder || 'Buscar por RUC o nombre...'"
        autocomplete="off"
        @input="onInput"
        @blur="handleBlur"
        @keydown.down.prevent="moverResaltado(1)"
        @keydown.up.prevent="moverResaltado(-1)"
        @keydown.esc.prevent="cerrarLista"
        @keydown.enter.prevent="handleEnter"
      />
      <ul v-if="listaVisible" class="provider-select__list">
        <li v-if="buscando" class="provider-select__item provider-select__item--muted">Buscando...</li>
        <li v-else-if="errorBusqueda" class="provider-select__item provider-select__item--error">
          No se pudo buscar. Reintentá.
        </li>
        <template v-else>
          <li
            v-for="(prov, index) in resultados"
            :key="prov.proveedor_id || prov.id || prov.nro_documento"
            class="provider-select__item"
            :class="{ 'is-active': index === resaltado }"
            @mousedown.prevent="elegirProveedor(prov)"
            @mouseenter="resaltado = index"
          >
            <strong>{{ prov.nro_documento || 'Sin RUC' }}</strong>
            <span>{{ prov.nombre || prov.nombre_fantasia || 'Sin nombre' }}</span>
          </li>
          <li v-if="!resultados.length" class="provider-select__item provider-select__item--muted">
            Sin coincidencias
          </li>
          <li
            class="provider-select__item provider-select__item--action"
            :class="{ 'is-active': resaltado === resultados.length }"
            @mousedown.prevent="registrarNuevo"
            @mouseenter="resaltado = resultados.length"
          >
            Registrar proveedor nuevo...
          </li>
        </template>
      </ul>
    </div>
  </div>

  <div v-else class="provider-select provider-select--bare">
    <input
      ref="inputEl"
      v-model="inputValue"
      class="form-control"
      :readonly="disabled"
      :placeholder="placeholder || 'Buscar por RUC o nombre...'"
      autocomplete="off"
      @input="onInput"
      @blur="handleBlur"
      @keydown.down.prevent="moverResaltado(1)"
      @keydown.up.prevent="moverResaltado(-1)"
      @keydown.esc.prevent="cerrarLista"
      @keydown.enter.prevent="handleEnter"
    />
    <ul v-if="listaVisible" class="provider-select__list">
      <li v-if="buscando" class="provider-select__item provider-select__item--muted">Buscando...</li>
      <li v-else-if="errorBusqueda" class="provider-select__item provider-select__item--error">
        No se pudo buscar. Reintentá.
      </li>
      <template v-else>
        <li
          v-for="(prov, index) in resultados"
          :key="prov.proveedor_id || prov.id || prov.nro_documento"
          class="provider-select__item"
          :class="{ 'is-active': index === resaltado }"
          @mousedown.prevent="elegirProveedor(prov)"
          @mouseenter="resaltado = index"
        >
          <strong>{{ prov.nro_documento || 'Sin RUC' }}</strong>
          <span>{{ prov.nombre || prov.nombre_fantasia || 'Sin nombre' }}</span>
        </li>
        <li v-if="!resultados.length" class="provider-select__item provider-select__item--muted">
          Sin coincidencias
        </li>
        <li
          class="provider-select__item provider-select__item--action"
          :class="{ 'is-active': resaltado === resultados.length }"
          @mousedown.prevent="registrarNuevo"
          @mouseenter="resaltado = resultados.length"
        >
          Registrar proveedor nuevo...
        </li>
      </template>
    </ul>
  </div>
</template>

<script>
import apiService from '@/services/apiService.js';

// Umbral y espera de la búsqueda. Dos caracteres evitan disparar una consulta
// con la primera tecla; 300 ms es lo que tarda una pausa natural de tipeo.
const MIN_CARACTERES = 2;
const DEBOUNCE_MS = 300;

export default {
  name: 'ProviderSelect',
  emits: ['provider-selected', 'register', 'update:modelValue', 'input-blur', 'input-enter'],
  props: {
    modelValue: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    bare: { type: Boolean, default: false },
    placeholder: { type: String, default: 'Buscar por RUC o nombre...' },
    // Se conserva por compatibilidad con los consumidores actuales. Ya no
    // cambia el comportamiento de búsqueda: el autocompletado es el mismo en
    // los dos modos, porque tener dos búsquedas distintas según la pantalla
    // era justamente el problema (RF-3 del SPEC).
    noList: { type: Boolean, default: false }
  },
  data() {
    return {
      resultados: [],
      buscando: false,
      errorBusqueda: false,
      listaAbierta: false,
      resaltado: -1,
      debounceId: null,
      ultimaConsulta: ''
    };
  },
  computed: {
    inputValue: {
      get() { return this.modelValue; },
      set(val) { this.$emit('update:modelValue', val); }
    },
    listaVisible() {
      return this.listaAbierta && !this.disabled;
    }
  },
  beforeUnmount() {
    if (this.debounceId) clearTimeout(this.debounceId);
  },
  methods: {
    focus() {
      this.$refs.inputEl?.focus?.();
      this.$refs.inputEl?.select?.();
    },
    cerrarLista() {
      this.listaAbierta = false;
      this.resaltado = -1;
    },
    onInput() {
      const texto = String(this.inputValue || '').trim();
      if (this.debounceId) clearTimeout(this.debounceId);
      this.errorBusqueda = false;

      if (texto.length < MIN_CARACTERES) {
        this.resultados = [];
        this.cerrarLista();
        return;
      }
      this.debounceId = setTimeout(() => this.buscar(texto), DEBOUNCE_MS);
    },
    // Un solo parámetro `search` cubre RUC y nombre: el backend ya busca con
    // ILIKE sobre nombre, nombre_fantasia, contacto, nro_documento, ruc y ci
    // (models/Providers.js). No hace falta ningún endpoint nuevo.
    async buscar(texto) {
      this.buscando = true;
      this.listaAbierta = true;
      this.ultimaConsulta = texto;
      try {
        // `apiService.get(url, params)` recibe los params DIRECTO, no un config
        // de axios: envolverlos en `{ params: ... }` los anida y la query sale
        // como `params[search]=`, que el backend ignora en silencio.
        const { data } = await apiService.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/providers`,
          { search: texto, limit: 8, page: 1 }
        );
        // Respuesta paginada: `{ data: [...], pagination: {...} }`. Leer `data`
        // a secas dejaba la lista siempre vacía — era el defecto C-2 del SPEC.
        const lista = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        // Si mientras tanto el operador siguió tipeando, esta respuesta ya no
        // corresponde a lo que hay en pantalla y se descarta.
        if (this.ultimaConsulta !== texto) return;
        this.resultados = lista;
        this.resaltado = lista.length ? 0 : -1;
        this.errorBusqueda = false;
      } catch (error) {
        if (this.ultimaConsulta !== texto) return;
        console.error('Error buscando proveedores:', error);
        this.resultados = [];
        this.resaltado = -1;
        // Un fallo de red o un 500 muestran el error, no abren el alta: eso
        // convertía un problema de conexión en un formulario de carga.
        this.errorBusqueda = true;
      } finally {
        if (this.ultimaConsulta === texto) this.buscando = false;
      }
    },
    moverResaltado(delta) {
      if (!this.listaVisible || this.buscando || this.errorBusqueda) return;
      const total = this.resultados.length + 1; // +1 por la fila de acción
      if (!total) return;
      this.resaltado = (this.resaltado + delta + total) % total;
    },
    elegirProveedor(prov) {
      this.$emit('provider-selected', prov);
      this.inputValue = prov.nro_documento || '';
      this.resultados = [];
      this.cerrarLista();
    },
    // Si lo tipeado es sólo dígitos se precarga como RUC; si tiene letras, es
    // un nombre y el RUC queda vacío. Precargar un nombre en el campo RUC era
    // el síntoma exacto que reportó el usuario.
    registrarNuevo() {
      const texto = String(this.inputValue || '').trim();
      const esDocumento = /^[0-9-]+$/.test(texto);
      this.cerrarLista();
      this.$emit('register', {
        documento: esDocumento ? texto : '',
        nombre: esDocumento ? '' : texto
      });
    },
    handleEnter() {
      if (this.listaVisible && !this.buscando && !this.errorBusqueda) {
        if (this.resaltado >= 0 && this.resaltado < this.resultados.length) {
          this.elegirProveedor(this.resultados[this.resaltado]);
          return;
        }
        if (this.resaltado === this.resultados.length) {
          this.registrarNuevo();
          return;
        }
      }
      this.$emit('input-enter', this.inputValue);
    },
    handleBlur() {
      // El cierre se difiere: un `mousedown` sobre la lista tiene que poder
      // resolverse antes de que el blur la desmonte.
      setTimeout(() => this.cerrarLista(), 150);
      this.$emit('input-blur', this.inputValue);
    },
    // Se conserva para los consumidores que la llaman tras registrar un
    // proveedor. Ya no precarga un listado completo: limpia el estado para que
    // la próxima búsqueda salga fresca.
    async loadProviders() {
      this.resultados = [];
      this.ultimaConsulta = '';
      this.cerrarLista();
    }
  }
};
</script>

<style scoped>
.form-label {
  font-weight: bold;
}

.provider-select {
  position: relative;
}

.provider-select--bare {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
}

.provider-select__field {
  position: relative;
}

.provider-select__list {
  position: absolute;
  z-index: 1080;
  top: 100%;
  left: 0;
  right: 0;
  margin: 2px 0 0;
  padding: 0;
  list-style: none;
  max-height: 260px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 6px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
}

.provider-select__item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 0.88rem;
  border-bottom: 1px solid #f1f3f5;
}

.provider-select__item:last-child {
  border-bottom: none;
}

.provider-select__item.is-active {
  background-color: #e7f1ff;
}

.provider-select__item span {
  color: #495057;
}

.provider-select__item--muted,
.provider-select__item--error {
  cursor: default;
  color: #6c757d;
}

.provider-select__item--error {
  color: #b02a37;
}

.provider-select__item--action {
  font-weight: 600;
  color: #0d6efd;
}
</style>
